import { ColumnDef } from '@tanstack/react-table';
import { Eye, MoreHorizontal, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createDateColumn, createSortableColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { StockReservation, StockReservationStatus } from '../types/stockReservation';

type Role = { key: string; name: string; description: string };
type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const statusVariant: Record<StockReservationStatus, 'default' | 'secondary' | 'warning'> = {
  active: 'default',
  released: 'secondary',
  expired: 'warning',
};

export default function Reservations() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [rows, setRows] = useState<StockReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('user');
      if (saved) setUser(JSON.parse(saved));
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    const q = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      status,
      sortBy: 'created_at',
      sortOrder: 'DESC',
    });
    if (debouncedSearch) q.set('search', debouncedSearch);
    fetch(`/api/stock-reservations?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setRows(data.data);
          setPages(data.pagination.pages);
          setTotal(data.pagination.total);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [page, limit, status, debouncedSearch]);

  const columns: ColumnDef<StockReservation>[] = [
    {
      ...createSortableColumn<StockReservation>('project_code', 'Project Code'),
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/reservations/${row.original.id}`)}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.project_code}
        </button>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={statusVariant[row.original.status]}>
          {row.original.status.replace('_', ' ').toUpperCase()}
        </Badge>
      ),
    },
    {
      id: 'reserved_by',
      header: 'Reserved By',
      cell: ({ row }) => {
        const u = row.original.reserved_by_user;
        return u ? `${u.first_name} ${u.last_name}` : '-';
      },
    },
    {
      id: 'items',
      header: 'Units',
      cell: ({ row }) => row.original.items?.length ?? 0,
    },
    createDateColumn<StockReservation>('expires_at', 'Expires', 'datetime'),
    createDateColumn<StockReservation>('created_at', 'Created', 'datetime'),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/reservations/${row.original.id}`)}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </DropdownMenuItem>
            {row.original.status === 'active' && (
              <DropdownMenuItem onClick={() => navigate(`/reservations/new?reservationId=${row.original.id}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Add products
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="reservations" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-heading font-bold text-primary leading-tight">Reservations</h1>
          <Button onClick={() => navigate('/reservations/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Reservation
          </Button>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          searchKey="project_code"
          searchPlaceholder="Search project code..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide
          pagination={{ total, page, limit, pages }}
          onPaginationChange={(newPage, newLimit) => {
            setPage(newPage);
            setLimit(newLimit);
          }}
          toolbarContentLeft={
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          }
          emptyMessage="No reservations found."
        />
      </main>
    </div>
  );
}
